import React, { useState } from 'react';
import { AdminLayout } from '@/components/AdminLayout';
import { Link, router, useForm } from '@inertiajs/react';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

type Faq = {
  id?: number;
  question: string;
  answer: string;
  category: string;
  position: number;
  published: boolean;
};

type Props = {
  faq: Faq;
  categories: string[];
  errors?: string[];
};

export default function FaqForm({ faq, categories, errors }: Props) {
  const [showNewCategory, setShowNewCategory] = useState(false);
  const { data, setData, processing, submit } = useForm({
    question: faq.question || '',
    answer: faq.answer || '',
    category: faq.category || 'general',
    position: faq.position || 0,
    published: faq.published ?? true,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (faq.id) {
      router.patch(`/admin/faqs/${faq.id}`, data);
    } else {
      router.post('/admin/faqs', data);
    }
  };

  const handleCategoryChange = (value: string) => {
    if (value === 'new') {
      setShowNewCategory(true);
      setData('category', '');
    } else {
      setShowNewCategory(false);
      setData('category', value);
    }
  };

  return (
    <AdminLayout title={faq.id ? 'Edit FAQ' : 'New FAQ'}>
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Link
            href="/admin/faqs"
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to FAQs
          </Link>
          <h1 className="mt-2 text-2xl font-semibold text-gray-900">
            {faq.id ? 'Edit FAQ' : 'New FAQ'}
          </h1>
        </div>

        {/* Error messages */}
        {errors && errors.length > 0 && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm font-medium text-red-800">Please fix the following errors:</p>
            <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6 bg-white shadow rounded-lg p-6">
          <div>
            <Label htmlFor="question">Question *</Label>
            <Input
              id="question"
              type="text"
              value={data.question}
              onChange={(e) => setData('question', e.target.value)}
              required
              className="mt-1"
              placeholder="What is your question?"
            />
          </div>

          <div>
            <Label htmlFor="answer">Answer *</Label>
            <Textarea
              id="answer"
              value={data.answer}
              onChange={(e) => setData('answer', e.target.value)}
              required
              rows={6}
              className="mt-1"
              placeholder="Provide a detailed answer..."
            />
          </div>

          <div>
            <Label htmlFor="category">Category *</Label>
            {!showNewCategory ? (
              <Select
                value={data.category}
                onValueChange={handleCategoryChange}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.length === 0 && (
                    <SelectItem value="general">General</SelectItem>
                  )}
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat.charAt(0).toUpperCase() + cat.slice(1)}
                    </SelectItem>
                  ))}
                  <SelectItem value="new">+ Add New Category</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <div className="flex gap-2 mt-1">
                <Input
                  type="text"
                  value={data.category}
                  onChange={(e) => setData('category', e.target.value)}
                  placeholder="Enter new category name"
                  required
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowNewCategory(false);
                    setData('category', categories[0] || 'general');
                  }}
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="position">Position (Order)</Label>
            <Input
              id="position"
              type="number"
              value={data.position}
              onChange={(e) => setData('position', parseInt(e.target.value) || 0)}
              className="mt-1"
              min="0"
            />
            <p className="mt-1 text-xs text-gray-500">
              Lower numbers appear first within the category
            </p>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Label htmlFor="published" className="text-base font-medium">
                Published
              </Label>
              <Switch
                id="published"
                checked={data.published}
                onCheckedChange={(checked) => setData('published', checked)}
              />
            </div>
            <p className="text-sm text-gray-500">
              {data.published ? 'FAQ is visible to users' : 'FAQ is hidden from users'}
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-6 border-t">
            <Button type="button" variant="outline" asChild>
              <Link href="/admin/faqs">Cancel</Link>
            </Button>
            <Button type="submit" disabled={processing}>
              {processing ? 'Saving...' : 'Save FAQ'}
            </Button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
}